import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaCalendarAlt, FaHome } from 'react-icons/fa';
import RegisterPaymentModal from '../../../Components/RegisterPaymentModal/RegisterPaymentModal';
import {
  RENTAL_TYPE,
  normalizePayment,
  formatCurrency,
  formatDate,
  getDetailPath
} from '../utils/rentalTypeHelpers';
import style from './PendingPayments.module.css';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getDaysUntilEndOfMonth = () => {
  const today = new Date();
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const diffMs = endOfMonth - today;
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(days, 1);
};

const PendingPayments = () => {
  const navigate = useNavigate();
  const [regularPayments, setRegularPayments] = useState([]);
  const [adminPayments, setAdminPayments] = useState([]);
  const [regularSummary, setRegularSummary] = useState(null);
  const [adminSummary, setAdminSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [activePayment, setActivePayment] = useState(null);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError('');
      const days = getDaysUntilEndOfMonth();

      const [regResp, admResp] = await Promise.allSettled([
        axios.get(`/rentals/admin/payments/upcoming?days=${days}`),
        axios.get(`/admin-rentals/payments/upcoming?days=${days}`)
      ]);

      // Regular
      if (regResp.status === 'fulfilled') {
        const payload = regResp.value.data?.data || {};
        const payments = Array.isArray(payload.upcomingPayments)
          ? payload.upcomingPayments
          : [];
        setRegularPayments(
          payments.filter((p) => p.paymentStatus === 'pending')
        );
        setRegularSummary(payload.summary || null);
      }

      // Admin
      if (admResp.status === 'fulfilled') {
        const payload = admResp.value.data?.data || {};
        const payments = Array.isArray(payload.upcomingPayments)
          ? payload.upcomingPayments
          : Array.isArray(payload)
          ? payload
          : [];
        setAdminPayments(
          payments.filter((p) => (p.status || p.paymentStatus) === 'pending')
        );
        setAdminSummary(payload.summary || null);
      }

      if (regResp.status === 'rejected' && admResp.status === 'rejected') {
        const err = regResp.reason;
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            err.message
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  // Combinar
  const combined = useMemo(() => {
    const reg = (regularPayments || []).map((p) => ({
      _type: RENTAL_TYPE.REGULAR,
      raw: p,
      ...normalizePayment(p, RENTAL_TYPE.REGULAR),
      daysUntilDue: toNumber(p.daysUntilDue),
      alertLevel: p.alertLevel
    }));
    const adm = (adminPayments || []).map((p) => ({
      _type: RENTAL_TYPE.ADMIN,
      raw: p,
      ...normalizePayment(p, RENTAL_TYPE.ADMIN),
      daysUntilDue: toNumber(p.daysUntilDue),
      alertLevel: p.alertLevel
    }));
    return [...reg, ...adm].sort((a, b) => {
      const da = new Date(a.dueDate || 0).getTime();
      const db = new Date(b.dueDate || 0).getTime();
      return da - db;
    });
  }, [regularPayments, adminPayments]);

  const filtered = useMemo(() => {
    if (typeFilter === 'all') return combined;
    return combined.filter((p) => p._type === typeFilter);
  }, [combined, typeFilter]);

  const totalPendingAmount = useMemo(
    () =>
      filtered.reduce(
        (acc, payment) => acc + toNumber(payment.totalAmount),
        0
      ),
    [filtered]
  );

  const getAlertLevel = (payment) => {
    if (payment?.alertLevel) return payment.alertLevel;
    const daysUntilDue = toNumber(payment?.daysUntilDue);
    if (daysUntilDue <= 3) return 'danger';
    if (daysUntilDue <= 7) return 'warning';
    return 'info';
  };

  const getAlertLabel = (level) => {
    if (level === 'danger') return 'Vence ya';
    if (level === 'warning') return 'Próximo';
    return 'A tiempo';
  };

  if (loading) {
    return (
      <div className={style.loading}>
        Cargando pagos pendientes del mes...
      </div>
    );
  }
  if (error) {
    return <div className={style.error}>Error: {error}</div>;
  }

  const totalCount =
    (regularSummary?.totalPayments ?? regularPayments.length) +
    (adminSummary?.totalPayments ?? adminPayments.length);
  const totalAmountSummary =
    toNumber(regularSummary?.totalAmount) +
    toNumber(adminSummary?.totalAmount);
  const dangerAlerts =
    toNumber(regularSummary?.dangerAlerts) +
    toNumber(adminSummary?.dangerAlerts);
  const warningAlerts =
    toNumber(regularSummary?.warningAlerts) +
    toNumber(adminSummary?.warningAlerts);

  return (
    <div className={style.container}>
      <div className={style.header}>
        <h1 className={style.title}>
          <FaCalendarAlt className={style.titleIcon} />
          Pagos Pendientes del Mes
        </h1>

        <div className={style.summary}>
          <div className={style.summaryItem}>
            <span className={style.summaryLabel}>Cuotas pendientes:</span>
            <span className={style.summaryValue}>{totalCount}</span>
          </div>
          <div className={style.summaryItem}>
            <span className={style.summaryLabel}>Monto pendiente:</span>
            <span className={`${style.summaryValue} ${style.amount}`}>
              {formatCurrency(totalAmountSummary || totalPendingAmount)}
            </span>
          </div>
          <div className={style.summaryItem}>
            <span className={style.summaryLabel}>Alertas críticas:</span>
            <span className={`${style.summaryValue} ${style.danger}`}>
              {dangerAlerts ||
                filtered.filter((p) => getAlertLevel(p) === 'danger').length}
            </span>
          </div>
          <div className={style.summaryItem}>
            <span className={style.summaryLabel}>Alertas próximas:</span>
            <span className={`${style.summaryValue} ${style.warning}`}>
              {warningAlerts ||
                filtered.filter((p) => getAlertLevel(p) === 'warning')
                  .length}
            </span>
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          padding: '12px 0',
          flexWrap: 'wrap'
        }}
      >
        {[
          { v: 'all', l: 'Todos' },
          { v: RENTAL_TYPE.REGULAR, l: 'Regular' },
          { v: RENTAL_TYPE.ADMIN, l: 'Solo Admin' }
        ].map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => setTypeFilter(opt.v)}
            style={{
              padding: '6px 12px',
              borderRadius: 999,
              border: 'none',
              cursor: 'pointer',
              background: typeFilter === opt.v ? '#1e40af' : '#e5e7eb',
              color: typeFilter === opt.v ? '#fff' : '#374151',
              fontWeight: 600,
              fontSize: '0.85rem'
            }}
          >
            {opt.l}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className={style.noData}>
          <FaCalendarAlt className={style.noDataIcon} />
          <h3>No hay pagos pendientes para este mes</h3>
          <p>Todo está al día por ahora.</p>
        </div>
      ) : (
        <div className={style.list}>
          {filtered.map((payment) => {
            const alertLevel = getAlertLevel(payment);
            const isAdmin = payment._type === RENTAL_TYPE.ADMIN;

            return (
              <div
                key={`${payment._type}-${payment.id}`}
                className={`${style.card} ${style[alertLevel]}`}
              >
                <div className={style.cardTop}>
                  <div className={style.property}>
                    <FaHome />
                    <div>
                      <h3>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 999,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            background: isAdmin ? '#fef3c7' : '#dbeafe',
                            color: isAdmin ? '#92400e' : '#1e40af',
                            marginRight: 8,
                            verticalAlign: 'middle'
                          }}
                        >
                          {isAdmin ? 'Admin' : 'Regular'}
                        </span>
                        {payment.propertyAddress}
                      </h3>
                      <p>Propietario: {payment.ownerName}</p>
                    </div>
                  </div>

                  <div className={style.amountBlock}>
                    <span
                      className={`${style.alertBadge} ${style[alertLevel]}`}
                    >
                      {getAlertLabel(alertLevel)}
                    </span>
                    <span>Monto</span>
                    <strong>{formatCurrency(payment.totalAmount)}</strong>
                  </div>
                </div>

                <div className={style.cardBottom}>
                  <span>
                    Vence: {formatDate(payment.dueDate)}
                    {' • '}
                    {toNumber(payment.daysUntilDue)} días
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className={style.detailBtn}
                      onClick={() => setActivePayment(payment)}
                    >
                      Registrar pago
                    </button>
                    <button
                      type="button"
                      className={style.detailBtn}
                      onClick={() =>
                        navigate(
                          getDetailPath(
                            payment.rentalId,
                            payment._type,
                            'pagos'
                          )
                        )
                      }
                    >
                      Ver contrato/pagos
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={style.footerAction}>
        <button
          type="button"
          className={style.backBtn}
          onClick={() => navigate('/alquileres')}
        >
          Volver al dashboard
        </button>
        <button
          type="button"
          className={style.primaryBtn}
          onClick={() => navigate('/alquileres/vencidos')}
        >
          Ir a pagos vencidos
        </button>
      </div>

      {activePayment && (
        <RegisterPaymentModal
          paymentId={activePayment.id}
          paymentInfo={activePayment}
          type={activePayment._type}
          onClose={() => setActivePayment(null)}
          onRegistered={() => {
            setActivePayment(null);
            loadAll();
          }}
        />
      )}
    </div>
  );
};

export default PendingPayments;
